<template>
  <ScheduleADetailsModal
    v-if="detailsModalVisible"
    :ingredient="detailsModalIngredient"
    @closeModal="detailsModalVisible = false"
  />
  <CopyIngredientsModal
    v-if="copyIngredientsModalVisible"
    :plants="plants"
    :ingredientIds="selectedIngredientIds"
    @closeModal="onCopyModalClosed"
  />
  <DeletionModal
    v-if="deletionModalVisible"
    :plants="plants"
    :ingredientIds="selectedIngredientIds"
    @closeModal="onDeletionModalClosed"
  />
  <IngredientApplicationModal
    v-if="ingredientApplicationModalVisible"
    :plants="plants"
    @closeModal="ingredientApplicationModalVisible = false"
  />
  <div class="schedule-a-table" data-cy="schedule-a-table">
    <div class="grid-control__container" data-cy="header-container">
      <div v-if="canModifyCarts" class="schedule-a-table__actions">
        <Button
          type="button"
          theme-color="primary"
          :disabled="!selectedIngredientIds.length"
          @click="onCopyClicked"
          data-cy="copy-ingredients-button"
          >Add Selected Ingredients</Button
        >
        <Button
          type="button"
          theme-color="error"
          :disabled="!selectedIngredientIds.length"
          @click="onDeletionClicked"
          data-cy="delete-ingredients-button"
          >Delete Selected Ingredients</Button
        >
        <span>OR</span>
        <Button
          type="button"
          theme-color="success"
          @click="onAddClicked"
          data-cy="add-ingredients-button"
          >Enter New Ingredients</Button
        >
      </div>
      <div class="pull-right">
        <TableSearch
          class="schedule-a-search"
          :value="searchInput"
          placeholder="Search by Name or Brand or Plant"
          @searchSubmitted="onSearchSubmitted"
        />
        <GridActionsMenu
          @actionClicked="onActionClicked"
          data-cy="grid-actions"
        />
      </div>
    </div>
    <ScheduleAGrid
      :filter="filter"
      :ingredients="ingredients"
      :loading="ingredientsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalIngredients"
      :canModifyCarts="canModifyCarts"
      :sort="sortDescriptors"
      ref="gridRef"
      :getPropValues="getPropValuesAsync"
      @ingredientsChanged="onIngredientsChanged"
      @selectionChanged="onSelectionChanged"
      @detailsClicked="onDetailsClicked"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import GridActionsMenu from "@/shared/ui/inputs/GridActionsMenu.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watch } from "vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ScheduleAGrid from "@/views/scheduleA/ScheduleAGrid.vue";
import type { IdAndName } from "@/Domain/IdAndName";
import {
  getJsonFilterString,
  valueFilterConvert,
  FilterValuesTake,
  boolYNConvertBack,
  boolYNConvert,
} from "@/shared/helpers/filtersHelper";
import type {
  IngredientWithSelection,
  GetIngredientDisplayScheduleAWithFilteringRequest,
} from "@/api/scheduleA";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import ScheduleADetailsModal from "@/views/scheduleA/ScheduleADetailsModal.vue";
import DeletionModal from "@/views/scheduleA//DeletionModal.vue";
import CopyIngredientsModal from "./CopyIngredientsModal.vue";
import IngredientApplicationModal from "./IngredientApplicationModal.vue";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import {
  convertBackToNumber,
  convertNumber,
} from "@/Domain/EnumGroupConverter";
import { EnumDpm } from "@/Domain/EnumDpm";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { EnumUsedIn1Status } from "@/Domain/EnumUsedIn1Status";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import enumHelper from "@/shared/helpers/enumHelper";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import type { PlantWithSelection } from "@/api/scheduleB";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const props = defineProps<{
  plants: IdAndName[];
  canModifyCarts: boolean;
}>();

const defaultTake = 100;

const ingredients = ref<IngredientWithSelection[]>([]);
const selectedIngredientIds = ref<number[]>([]);
const totalIngredients = ref<number>(0);
const ingredientsLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(defaultTake);
const searchInput = ref<string>("");
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const detailsModalVisible = ref<boolean>(false);
const deletionModalVisible = ref<boolean>(false);
const copyIngredientsModalVisible = ref<boolean>(false);
const ingredientApplicationModalVisible = ref<boolean>(false);

const gridRef = ref<InstanceType<typeof ScheduleAGrid> | null>(null);
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const tableName = "ScheduleA";

const detailsModalIngredient = ref<IngredientWithSelection>(
  {} as IngredientWithSelection
);

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});

const valueFiltersConvertOptions = [
  {
    column: "_Dpm",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumDpm[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumDpm, value);
      return EnumDpm[key as keyof typeof EnumDpm];
    },
  },
  {
    column: "VueScheduleAGroup",
    convert: (value: number) => {
      return convertNumber(value);
    },
    convertBack: (value: string) => {
      return convertBackToNumber(value);
    },
  },
  {
    column: "VueScheduleAStipulationExists",
    convert: boolYNConvert,
    convertBack: boolYNConvertBack,
  },
  {
    column: "_Pesach",
    convert: boolYNConvert,
    convertBack: boolYNConvertBack,
  },
  {
    column: "VueScheduleAUsedInOneStatus",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumUsedIn1Status[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumUsedIn1Status, value);
      return EnumUsedIn1Status[key as keyof typeof EnumUsedIn1Status];
    },
  },
  {
    column: "_PlantStatus",
    convert: (value: number) => {
      return EnumCompanyStatus[value];
    },
    convertBack: (value: string) => {
      return EnumCompanyStatus[value as keyof typeof EnumCompanyStatus];
    },
  },
] as ValueFieldConvertOption[];

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const getIngredientDisplayScheduleAWithFiltering = async function (
  valuesForFieldOnly?: string
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchInput.value,
    plantList: props.plants.map((p) => p.ID).join(";"),
    allPlantCompanies: false,
    maxInstancesToReturn: 0,
    filters: getJsonFilterString(filter.value, valueFiltersConvertOptions),
    take: take.value,
    skip: skip.value,
    sortField: sortField.value,
    ascending: sortAscending.value,
  } as GetIngredientDisplayScheduleAWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetIngredientDisplayScheduleAWithFiltering", requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(
    createCacheKey(requestParams, "GetIngredientDisplayScheduleAWithFiltering"),
    () => api.get("GetIngredientDisplayScheduleAWithFiltering", requestParams)
  );
};

const getIngredientsAsync = async function () {
  if (ingredientsLoading.value) return;

  ingredientsLoading.value = true;

  await handleResponsePromiseAsync(
    getIngredientDisplayScheduleAWithFiltering(),
    (data) => {
      ingredients.value = data.ListItems;
      totalIngredients.value = data.TotalItemsInList;
    },
    "ScheduleATable.vue",
    "getIngredientsAsync"
  );

  ingredientsLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: IngredientWithSelection[] = [];
  await handleResponsePromiseAsync(
    getIngredientDisplayScheduleAWithFiltering(column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "ScheduleATable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const clearSelection = function () {
  selectedIngredientIds.value = [];
  ingredients.value.forEach((p) => (p.selected = false));
};

const onIngredientsChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    filter.value.filters = options.filterEvent.filter.filters;
  }
  if (take.value != options.take) {
    take.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  skip.value = options.skip;
  getIngredientsAsync();
};

const onDetailsClicked = function (ingredient: IngredientWithSelection) {
  detailsModalIngredient.value = ingredient;
  detailsModalVisible.value = true;
};

const onCopyModalClosed = function (cartCount: number) {
  copyIngredientsModalVisible.value = false;
  if (cartCount > -1) {
    clearSelection();
  }
};

const onDeletionModalClosed = function (cartCount: number) {
  deletionModalVisible.value = false;
  if (cartCount > -1) {
    clearSelection();
  }
};

const onCopyClicked = function () {
  resetPlantSelection();
  copyIngredientsModalVisible.value = true;
};

const onDeletionClicked = function () {
  deletionModalVisible.value = true;
};

const onAddClicked = function () {
  ingredientApplicationModalVisible.value = true;
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  skip.value = 0;
  getIngredientsAsync();
};

const resetPlantSelection = function () {
  (props.plants as PlantWithSelection[]).forEach((p) => (p.selected = false));
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getIngredientsAsync();
};

const onSelectionChanged = function (
  changedIngredients: IngredientWithSelection[]
) {
  changedIngredients.forEach((ingredient) => {
    const index = selectedIngredientIds.value.findIndex(
      (p) => p === ingredient.UsedInOneId
    );
    if (ingredient.selected) {
      if (index === -1)
        selectedIngredientIds.value.push(ingredient.UsedInOneId);
      else
        console.debug(
          `ScheduleATable.vue: onSelectionChanged(): ingredient already selected`,
          ingredient,
          selectedIngredientIds.value[index]
        );
    } else if (index > -1) {
      selectedIngredientIds.value.splice(index, 1);
    } else {
      // If ingredient is deselected and it is not
      // in the selectedIngredientIds array - do nothing
    }
  });
};

const clearFilters = function () {
  console.debug("ScheduleATable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  searchInput.value = "";
  sortField.value = "";
  sortAscending.value = false;
  getIngredientsAsync();
};

const resetPagination = function () {
  if (take.value === defaultTake) return;

  skip.value = 0;
  take.value = defaultTake;
  GridOptionsService.clearPageSizeAsync(tableName);
  getIngredientsAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const onActionClicked = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ClearAllFilters:
      clearFilters();
      break;
    case GridMenuActionType.ResetPagination:
      resetPagination();
      break;
    case GridMenuActionType.ResetColumnOrder:
      updateColumnMenuIcons();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetPagination();
      updateColumnMenuIcons();
      break;
  }
  if (gridRef.value) gridRef.value.executeAction(actionType);
};

watch(
  () => [props.plants],
  async () => {
    await getIngredientsAsync();
  }
);

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  await getIngredientsAsync();
});
</script>

<style lang="scss">
.schedule-a-table {
  .grid-control__container {
    display: flex;
    align-items: center;
  }

  &__actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;

    .schedule-a-search {
      width: 400px;
    }
  }
}
</style>
